import React from 'react';

import DataGrid from '@gogame/data-grid';
import { Grid } from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';
import {
  useScreenDispatch,
  useScreenGo,
  useScreenState,
  useScreenTitle,
} from '@gogame/screens';

import {
  COLUMN_ID,
  COLUMN_NAME,
  COLUMN_STATUS,
  COLUMN_AFFECTED_LEVEL,
  COLUMN_LAST_RUN,
  COLUMN_LAST_RUN_RESULTS,
  COLUMN_NEXT_RUN,
  COLUMN_CREATED_BY,
  COLUMN_CREATED_DATE,
  COLUMN_UPDATED_DATE,
  COLUMN_ACTIONS,
  SMART_RULES,
} from 'scenes/SmartRules/constants';
import { SmartRuleFormCreate } from 'scenes/SmartRules/SmartRuleForm';
import {
  AFFECTED_LEVEL,
  SECONDARY_AFFECTED_LEVEL,
} from 'scenes/SmartRules/SmartRuleForm/General';
import { useIsUpdating, useSmartRulesGrid } from 'scenes/SmartRules/data';
import {
  SET_TABLE_SETTINGS,
  REORDER_COLUMNS,
  COLLAPSE_COLUMNS,
} from 'scenes/SmartRules/screenReducer';
import { getColor } from 'scenes/SmartRules/components/Status';
import SmartRulesActions from 'scenes/SmartRules/components/Actions';

import BatchToolbar from './components/BatchToolbar';
import Toolbar from './components/Toolbar';
import Name from './components/Name';
import LastRunResults from './components/LastRunResults';

export const SmartRulesDataTable = () => {
  const [screenTitle] = useScreenTitle();
  const [childComponent, openChildComponent] = useChildComponent();
  const dispatch = useScreenDispatch();
  const go = useScreenGo();

  const { columns, tableSettings, filters, searchQuery } = useScreenState();

  const { data, isLoading, isRefetching } = useSmartRulesGrid({
    tableSettings,
    filters,
    searchQuery,
  });
  const isUpdating = useIsUpdating();

  const handleChangeSettings = (settings) => {
    dispatch({
      type: SET_TABLE_SETTINGS,
      payload: settings,
    });
  };

  const handleReorder = (columns) => {
    dispatch({
      type: REORDER_COLUMNS,
      payload: columns,
    });
  };

  const handleCollapse = (collapsed) => {
    dispatch({
      type: COLLAPSE_COLUMNS,
      payload: collapsed,
    });
  };

  const handleCreate = () => {
    openChildComponent(SmartRuleFormCreate, {
      onSubmit: (response) => {
        go(response.id);
      },
    });
  };

  return (
    <Grid container item xs direction={'column'}>
      <DataGrid
        rows={data?.rows}
        count={data?.count}
        title={screenTitle}
        loading={isLoading || isUpdating}
        fetching={isRefetching}
        onChangeSettings={handleChangeSettings}
        onReorder={handleReorder}
        onCollapse={handleCollapse}
        tableSettings={tableSettings}
        columnsState={columns}
        checkboxSelection={true}
        toolbar={<Toolbar onCreate={handleCreate} />}
        selectedRowsToolbarActions={({ selectedValues }) => (
          <BatchToolbar selectedValues={selectedValues} />
        )}
        storageKey={SMART_RULES}
        defaultColumnWidth={160}
        rowHeight={100}
        notInitedLabel={'No records'}
      >
        <DataGrid.Column
          field={COLUMN_ID}
          type={'text'}
          headerName={'ID'}
          width={90}
          fixed={true}
          resizable={false}
          align={'right'}
          headerAlign={'right'}
        />
        <DataGrid.Column
          field={COLUMN_NAME}
          headerName={'Name'}
          width={500}
          formatter={(value, row) => <Name value={value} row={row} />}
        />
        <DataGrid.Column
          field={COLUMN_STATUS}
          headerName={'Status'}
          type={'status'}
          width={163}
          getStatusColor={getColor}
          getValue={(value) => ({ id: value, name: value })}
        />
        <DataGrid.Column
          field={COLUMN_AFFECTED_LEVEL}
          headerName={'Affected Level'}
          type={'text'}
          width={120}
          formatter={(_, row) =>
            row?.target?.[SECONDARY_AFFECTED_LEVEL]?.name ||
            row?.target?.[AFFECTED_LEVEL]?.name
          }
        />
        <DataGrid.Column
          field={COLUMN_LAST_RUN_RESULTS}
          headerName={'Last Run Results'}
          width={194}
          formatter={(_, row) => (
            <LastRunResults row={row} openChildComponent={openChildComponent} />
          )}
          sortable={false}
        />
        <DataGrid.Column
          field={COLUMN_LAST_RUN}
          headerName={'Last Run'}
          width={160}
          type={'date'}
          template={'LLL'}
          timeZone={'local'}
        />
        <DataGrid.Column
          field={COLUMN_NEXT_RUN}
          headerName={'Next Run'}
          width={160}
          type={'date'}
          template={'LLL'}
          timeZone={'local'}
        />
        <DataGrid.Column
          field={COLUMN_UPDATED_DATE}
          headerName={'Last Updated'}
          width={160}
          type={'date'}
          template={'LLL'}
          timeZone={'local'}
        />
        <DataGrid.Column
          field={COLUMN_CREATED_BY}
          headerName={'Created by'}
          width={150}
          type={'text'}
        />
        <DataGrid.Column
          field={COLUMN_CREATED_DATE}
          headerName={'Created'}
          width={160}
          type={'date'}
          template={'LLL'}
          timeZone={'local'}
        />
        <DataGrid.Column
          actionsColDef
          field={COLUMN_ACTIONS}
          type={'actions'}
          actionsComponent={SmartRulesActions}
          onOpenChildComponent={openChildComponent}
          limit={3}
        />
      </DataGrid>
      {childComponent}
    </Grid>
  );
};
